<template>
  <dialog
    ref="dialog"
    :class="['dialog', `dialog-${placement}`, {card: open}]"
    @click="(event: Event) => dismissible && event.target === dialog && closeModal()"
    @close="
      open = false;
      emits('close');
    "
  >
    <form ref="form" method="dialog" class="card-body needs-validation" novalidate @submit="submit">
      <header v-if="$slots.title" class="d-flex no-wrap">
        <div class="flex-grow-1 me-1">
          <h5 v-if="$slots.title" class="card-title">
            <slot name="title"></slot>
          </h5>
          <h6 v-if="$slots.subTitle" class="card-subtitle mb-2 text-body-secondary">
            <slot name="subTitle"></slot>
          </h6>
        </div>
        <button
          v-if="dismissible"
          type="button"
          class="btn-close"
          :aria-label="translate('Close')"
          @click="closeModal"
        ></button>
      </header>
      <button
        v-else-if="dismissible"
        type="button"
        class="btn-close position-absolute top-0 end-0"
        style="margin: var(--bs-card-spacer-y) var(--bs-card-spacer-x)"
        :aria-label="translate('Close')"
        @click="closeModal"
      ></button>
      <slot></slot>
      <div v-if="$slots.buttons" class="d-flex justify-content-end gap-2 mt-4">
        <slot name="buttons">
          <BootstrapButton
            name="cancel"
            variant="ghost"
            value="cancel"
            formmethod="dialog"
            :disabled="loading"
            @click="closeModal"
          >
            {{ translate('Cancel') }}
          </BootstrapButton>
          <BootstrapButton
            type="submit"
            variant="primary"
            name="submit"
            value="default"
            :loading="loading"
          >
            {{ translate('Submit') }}
          </BootstrapButton>
        </slot>
      </div>
    </form>
  </dialog>
</template>
<script setup lang="ts">
import {useLocalisation} from '@/composables/useLocalisation';
import {type DialogDrawerProps} from '@/components/shared/custom/DialogDrawer/DialogDrawer.types';

const {
  loading,
  placement = 'right',
  modal = false,
  dismissible = true,
} = defineProps<DialogDrawerProps>();
const emits = defineEmits<{submit: [event: Event]; close: []}>();
const {translate} = useLocalisation();
/**
 * N.B. If a <dialog> is opened using the open attribute, it is non-modal.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/dialog}
 */
const form = useTemplateRef<HTMLFormElement>('form');
const dialog = useTemplateRef<HTMLDialogElement>('dialog');
const open = ref(false);
const openModal = () => {
  // Reset form validation state when opening
  form.value?.classList.remove('was-validated');
  // ::backdrop pseudo-element is displayed behind <dialog> when using HTMLDialogElement.showModal()
  if (modal) {
    dialog.value?.showModal();
  } else {
    dialog.value?.show(); // Display a non-modal dialog.
  }
  open.value = true;
};

const close = (returnValue?: string): void => dialog.value?.close(returnValue);
const closeModal = () => {
  close();
};
defineExpose({openModal, close, open});
/**
 * Leveraging Bootstrap's form validation
 * @see {@link https://getbootstrap.com/docs/5.3/forms/validation/}
 */
const submit = (event: Event) => {
  if (form.value?.checkValidity() === false) {
    event.preventDefault();
    event.stopPropagation();
    form.value.classList.add('was-validated');
  } else {
    event.preventDefault();
    emits('submit', event);
    // Automatically close the dialog if we're waiting making a request
    if (loading === undefined) close();
    form.value?.classList.remove('was-validated');
  }
};
</script>
<style lang="scss" scoped>
.dialog {
  z-index: 9999;
  padding: 0;
  background-color: var(--bs-body-bg);
}

/* TODO: Stylelint 17.0.0 complains */

/* This is bad for accessibility... */

/* .dialog::focus-visible {
  outline: none;
} */

.dialog::backdrop {
  cursor: pointer;
  background-color: var(--bs-dark);
  opacity: 0.5;
}

.dialog-top,
.dialog-bottom,
.dialog-left,
.dialog-right {
  position: fixed;
  border-radius: 0;
  border-width: 0;
}

.dialog-top,
.dialog-bottom {
  min-width: 100vw;
  width: 100vw;
  max-height: 20rem;
  height: min-content;
  margin-left: 0;
  margin-right: 0;
  border-radius: 0;
}

.dialog-left,
.dialog-right {
  max-width: 28rem;
  min-height: 100vh;
  height: 100vh;
  margin-top: 0;
  margin-bottom: 0;
  top: 0;
}

.dialog-centre {
  width: 100%;
  height: 100%;
  max-width: none; // Overwrite defaults
  max-height: none;
  border-radius: 0;
  border-width: 0;

  form {
    display: flex;
    flex-direction: column;
  }
}

@media (width >= 768px) {
  .dialog-centre {
    height: min-content;
    max-width: 28rem;
    max-height: 80vh;
    border-radius: var(--bs-card-border-radius);
    border-width: var(--bs-card-border-width);

    form {
      display: block;
      overflow-y: auto;
    }
  }
}

$delay: 0.3s;
$placements: 'top', 'right', 'bottom', 'left';
$width: var(--bs-card-border-width);

@each $placement in $placements {
  .dialog-#{$placement} {
    margin-#{$placement}: 0;
    #{$placement}: 0;

    @if $placement == 'top' {
      border-bottom-width: $width;
    } @else if $placement == 'right' {
      border-left-width: $width;
    } @else if $placement == 'bottom' {
      border-top-width: $width;
    } @else if $placement == 'left' {
      border-right-width: $width;
    }
  }
  .dialog-#{$placement}[open] {
    animation: fade-in-#{$placement} $delay ease-out;
  }
}

.dialog-centre[open] {
  animation: fade-in-centre $delay ease-out;
}

/* https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/dialog#animating_dialogs */

@keyframes fade-in-top {
  0% {
    opacity: 0;
    transform: translateY(-100%);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in-right {
  0% {
    opacity: 0;
    transform: translateX(100%);
  }

  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fade-in-bottom {
  0% {
    opacity: 0;
    transform: translateY(100%);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in-left {
  0% {
    opacity: 0;
    transform: translateX(-100%);
  }

  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fade-in-centre {
  0% {
    opacity: 0;
    transform: scale(99%);
  }

  100% {
    opacity: 1;
    transform: scale(100%);
  }
}
</style>
